import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getItem, depreciateItem, updateDepreciation } from '../../services'
import { DepreciationResponse } from '../../types'
import { Calculator, Edit2, History, TrendingDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import DepreciationHistory from './DepreciationHistory'

interface DepreciationEditorProps {
    readonly?: boolean
}

/**
 * DepreciationEditor - Displays and manages item depreciation
 * Features: View current/purchase value, calculate depreciation, manual override
 */
const DepreciationEditor = ({ readonly = false }: DepreciationEditorProps) => {
    const { id } = useParams<{ id: string }>()

    // State for modals
    const [showHistory, setShowHistory] = useState(false)
    const [showCalculate, setShowCalculate] = useState(false)
    const [showUpdate, setShowUpdate] = useState(false)
    const [months, setMonths] = useState(12)
    const [newRate, setNewRate] = useState<number>(0)
    const [newValue, setNewValue] = useState<number>(0)
    const [reason, setReason] = useState('')

    // Format currency (defined early for use in mutations)
    const formatCurrency = (value?: number) => {
        if (!value && value !== 0) return 'N/A'
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(value)
    }

    // Fetch item data
    const { data: item, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, id],
        queryFn: () => getItem(id!),
    })

    // Calculate depreciation mutation
    const calculateMutation = useMutationWithHandler(
        {
            mutationFn: () => {
                // Validate inputs
                if (months < 1 || months > 120) {
                    throw new Error('Months must be between 1 and 120')
                }
                return depreciateItem(id!, {
                    months,
                    reason: reason || undefined,
                })
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_ITEMS,
            onSuccess: (result: DepreciationResponse) => {
                setShowCalculate(false)
                setMonths(12)
                setReason('')
                // Custom success message with depreciated amount
                return `Depreciated ${formatCurrency(result.total_depreciated)}`
            },
            errorMessage: 'Failed to calculate depreciation',
        }
    )

    // Update depreciation mutation
    const updateMutation = useMutationWithHandler(
        {
            mutationFn: () => {
                // Validate inputs
                if (newRate < 0 || newRate > 100) {
                    throw new Error('Rate must be between 0% and 100%')
                }
                if (newValue < 0) {
                    throw new Error('Value cannot be negative')
                }
                return updateDepreciation(id!, {
                    depreciation_rate: newRate || undefined,
                    current_value: newValue || undefined,
                    reason: reason || undefined,
                })
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_ITEMS,
            successMessage: 'Depreciation settings updated',
            errorMessage: 'Failed to update depreciation',
            onSuccess: () => {
                setShowUpdate(false)
                setReason('')
            },
        }
    )

    // Format date
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'Never'
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    // Initialize update form with current values
    const openUpdateDialog = () => {
        setNewRate(item?.depreciation_rate || 0)
        setNewValue(item?.current_value || item?.purchase_price || 0)
        setShowUpdate(true)
    }

    // Render loading/error state
    if (renderState()) return renderState()

    const purchaseValue = item?.purchase_price || 0
    const currentValue = item?.current_value || purchaseValue
    const rate = item?.depreciation_rate || 0
    const depreciated = purchaseValue - currentValue
    const depreciationPercent =
        purchaseValue > 0 ? (depreciated / purchaseValue) * 100 : 0

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Depreciation
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Value Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <Label className="text-muted-foreground text-xs">
                            Purchase Value
                        </Label>
                        <p className="font-medium">
                            {formatCurrency(purchaseValue)}
                        </p>
                    </div>
                    <div>
                        <Label className="text-muted-foreground text-xs">
                            Current Value
                        </Label>
                        <p className="font-medium text-green-600">
                            {formatCurrency(currentValue)}
                        </p>
                    </div>
                    <div>
                        <Label className="text-muted-foreground text-xs">
                            Rate
                        </Label>
                        <p className="font-medium">{rate}% / year</p>
                    </div>
                    <div>
                        <Label className="text-muted-foreground text-xs">
                            Depreciated
                        </Label>
                        <p className="font-medium text-red-600">
                            {formatCurrency(depreciated)} (
                            {depreciationPercent.toFixed(1)}%)
                        </p>
                    </div>
                </div>

                {/* Last Updated */}
                <p className="text-xs text-muted-foreground">
                    Last depreciated: {formatDate(item?.last_depreciated_at)}
                </p>

                {/* Actions */}
                {!readonly && (
                    <div className="flex gap-2 pt-2 border-t">
                        {/* Calculate Dialog */}
                        <Dialog
                            open={showCalculate}
                            onOpenChange={setShowCalculate}
                        >
                            <DialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={rate <= 0 || currentValue <= 0}
                                >
                                    <Calculator className="w-4 h-4 mr-1" />
                                    Calculate
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>
                                        Calculate Depreciation
                                    </DialogTitle>
                                    <DialogDescription>
                                        Apply straight-line depreciation for
                                        specified months
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="months">Months</Label>
                                        <Input
                                            id="months"
                                            type="number"
                                            min={1}
                                            max={120}
                                            value={months}
                                            onChange={(e) =>
                                                setMonths(
                                                    parseInt(e.target.value) ||
                                                        1
                                                )
                                            }
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Estimated depreciation:{' '}
                                            {formatCurrency(
                                                ((currentValue * rate) /
                                                    100 /
                                                    12) *
                                                    months
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <Label htmlFor="reason">
                                            Reason (optional)
                                        </Label>
                                        <Input
                                            id="reason"
                                            value={reason}
                                            onChange={(e) =>
                                                setReason(e.target.value)
                                            }
                                            placeholder="e.g., Monthly depreciation"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowCalculate(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() =>
                                            calculateMutation.mutate()
                                        }
                                        disabled={calculateMutation.isPending}
                                    >
                                        {calculateMutation.isPending
                                            ? 'Calculating...'
                                            : 'Apply'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        {/* Update Settings Dialog */}
                        <Dialog open={showUpdate} onOpenChange={setShowUpdate}>
                            <DialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={openUpdateDialog}
                                >
                                    <Edit2 className="w-4 h-4 mr-1" />
                                    Edit Settings
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>
                                        Update Depreciation Settings
                                    </DialogTitle>
                                    <DialogDescription>
                                        Manually adjust depreciation rate or
                                        current value
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="rate">
                                            Annual Rate (%)
                                        </Label>
                                        <Input
                                            id="rate"
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.1}
                                            value={newRate}
                                            onChange={(e) =>
                                                setNewRate(
                                                    parseFloat(
                                                        e.target.value
                                                    ) || 0
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="value">
                                            Current Value (VND)
                                        </Label>
                                        <Input
                                            id="value"
                                            type="number"
                                            min={0}
                                            value={newValue}
                                            onChange={(e) =>
                                                setNewValue(
                                                    parseFloat(
                                                        e.target.value
                                                    ) || 0
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="updateReason">
                                            Reason (optional)
                                        </Label>
                                        <Input
                                            id="updateReason"
                                            value={reason}
                                            onChange={(e) =>
                                                setReason(e.target.value)
                                            }
                                            placeholder="e.g., Manual adjustment"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowUpdate(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => updateMutation.mutate()}
                                        disabled={updateMutation.isPending}
                                    >
                                        {updateMutation.isPending
                                            ? 'Saving...'
                                            : 'Save'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        {/* History Dialog */}
                        <Dialog
                            open={showHistory}
                            onOpenChange={setShowHistory}
                        >
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                    <History className="w-4 h-4 mr-1" />
                                    History
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                    <DialogTitle>
                                        Depreciation History
                                    </DialogTitle>
                                </DialogHeader>
                                <DepreciationHistory itemId={id!} />
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default DepreciationEditor
